import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSeoOverrides } from '../context/SeoOverridesContext';
import { useSiteSettings } from '../context/SiteSettingsContext';

const SITE_NAME = 'CHROBE One World';
const SITE_SUFFIX = ` | ${SITE_NAME}`;
// Google truncates SERP titles at ~60 chars. Only append the brand suffix if
// there's room left — otherwise the visible title becomes "…truncated | CHRO"
// which hurts CTR.
const MAX_TITLE_LEN = 60;

const _buildFullTitle = (pageTitle) => {
  if (!pageTitle) return `${SITE_NAME} — Premium Fahrzeugpflege Saarland`;
  // Skip suffix if it's already in the title (avoids "Kontakt — CHROBE One World | CHROBE One World")
  if (pageTitle.toLowerCase().includes(SITE_NAME.toLowerCase())) return pageTitle;
  const withSuffix = `${pageTitle}${SITE_SUFFIX}`;
  return withSuffix.length <= MAX_TITLE_LEN ? withSuffix : pageTitle;
};

// ─── Canonical-Host-Mapping ───────────────────────────────────────────────
// Emergent's CDN 308-redirects `chrobe.de` → `www.chrobe.de`, deshalb ist
// `www.chrobe.de` unser tatsächlich ausgelieferter Canonical-Host. Wenn wir
// hier `chrobe.de` verwenden, entsteht ein zirkulärer Canonical: die
// gecrawlte URL leitet weiter und der Canonical zeigt auf eine URL, die
// wieder weiterleitet — genau der Fehler den unser SEO-Audit als 6× kritisch
// meldet ("Canonical zeigt auf andere URL", "URL leitet weiter").
const CANONICAL_BASE = 'https://www.chrobe.de';

const _getCanonicalBase = () => CANONICAL_BASE;

const BASE_URL = _getCanonicalBase();
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;
const DEFAULT_DESC =
  // Default-Description: unter 160 Zeichen, mit Primär-Keyword „Aufbereiter" für SERP-Trigger.
  'CHROBE One World — dein Aufbereiter im Saarland für Fahrzeugpflege, Detailing, Keramikversiegelung & Lackpolitur. Mobil vor Ort — Termine online buchen.';

// Erweitertes Default-Keyword-Set: matched auf reale Google-Suchintent-Phrasen
// aus dem Saarland — inkl. „in der Nähe", „bei mir" und alle 11 Kernstädte.
const DEFAULT_KEYWORDS = [
  // Brand
  'CHROBE One World', 'One World', 'One World Eppelborn', 'One World Saarland',
  'CHROBE Eppelborn', 'Autopflege Eppelborn', 'Fahrzeugpflege Saarland',
  // In-der-Nähe / Standort-Suche (SEHR wichtig!)
  'Aufbereiter in der Nähe', 'Autoaufbereitung in der Nähe',
  'Fahrzeugaufbereitung in der Nähe', 'Auto polieren in der Nähe',
  'Autopflege in meiner Nähe', 'Aufbereiter bei mir', 'Detailing bei mir in der Nähe',
  'Keramikversiegelung in der Nähe', 'Aufbereiter Saarland in der Nähe',
  'mobile Autoaufbereitung Saarland', 'mobiler Aufbereiter Saarland',
  // Kernkeywords Saarland
  'Aufbereiter Saarland', 'Autoaufbereitung Saarland', 'Fahrzeugaufbereitung Saarland',
  'Fahrzeugpflege Saarland', 'Autopflege Saarland', 'Detailing Saarland',
  'Keramikversiegelung Saarland', 'Lackpolitur Saarland',
  'Auto polieren lassen Saarland', 'Auto aufbereiten lassen Saarland',
  'Auto polieren lassen Preis', 'Was kostet Autoaufbereitung',
  // Alle 11 primäre Städte
  'Aufbereiter Eppelborn', 'Aufbereiter Saarbrücken', 'Aufbereiter Neunkirchen',
  'Aufbereiter Saarlouis', 'Aufbereiter Homburg', 'Aufbereiter Merzig',
  'Aufbereiter St. Wendel', 'Aufbereiter Völklingen', 'Aufbereiter Sulzbach',
  'Aufbereiter Dillingen', 'Aufbereiter Lebach',
  'Autoaufbereitung Saarbrücken', 'Autoaufbereitung Neunkirchen',
  'Autoaufbereitung Saarlouis', 'Autoaufbereitung Homburg',
  'Detailing Saarbrücken', 'Detailing Neunkirchen',
  'Fahrzeugaufbereitung Eppelborn', 'Fahrzeugaufbereitung Saarbrücken',
  // Services
  'Keramikversiegelung', 'Nano-Versiegelung', 'Lackversiegelung',
  'Lackpolitur', 'Lackkorrektur', 'Autopolitur', 'Aufpolieren',
  'Innenraumreinigung', 'Autoinnenreinigung', 'Innenreinigung Auto',
  'Lederpflege', 'Ledersitze reinigen', 'Ledersitze aufbereiten',
  'Felgenreinigung', 'Alufelgen aufbereiten', 'Felgenversiegelung',
  'Cabrioverdeck reinigen', 'Cabrioverdeck imprägnieren',
  'Motorwäsche', 'Motorraum reinigen',
  'Kunststoffaufbereitung', 'Kunststoff schwarz aufbereiten',
  'Ozonbehandlung Auto', 'Geruch aus Auto entfernen',
  'Steinschlagreparatur', 'Kratzer entfernen Auto',
  // Long-Tail-Kaufabsicht
  'Auto aufbereiten Kosten', 'Autoaufbereitung Preise Saarland',
  'wie oft Auto polieren', 'wie lange hält Keramikversiegelung',
  'Auto vor Verkauf aufbereiten', 'Leasingrückgabe Aufbereitung',
  // B2B / Geschäftskunden
  'Flottenmanagement Saarland', 'Fuhrparkpflege Saarland',
  'Autohaus-Aufbereitung Saarland', 'Leasingrücknahme-Aufbereitung',
  'Fuhrpark Aufbereitung Geschäftskunden',
  // Abo
  'Pflege-Abo Auto', 'Auto Pflegeabonnement', 'Fahrzeugpflege Abo Saarland',
  // MyWorld digital
  'MyWorld digitale Garage', 'KI-Lackschaden-Scanner', 'Auto Care-Card',
  'Garage-Cockpit', 'Service-Historie digital',
  // Tools deutschlandweit
  'Waschbox Finder', 'SB-Waschbox finden', 'Waschbox Deutschland',
  'Waschboxen in der Nähe', 'Drehmoment Tabelle',
  // Werte
  'Premium Fahrzeugpflege', 'Auto Detailing Profi', 'Perfektion bis ins Detail',
].join(', ');

const SEO = ({ title, description, keywords, path, type = 'website', image, noIndex = false, jsonLd, breadcrumbs }) => {
  const { getOverride } = useSeoOverrides();
  const override = getOverride(path);

  // Override-Werte haben Vorrang vor den per-Page-Defaults; eingehende
  // Props haben Vorrang vor den globalen Defaults. Reihenfolge:
  //   override (Master-Edit) > prop > global default
  const effectiveTitle = override?.title || title;
  const effectiveDesc = override?.description || description || DEFAULT_DESC;
  const effectiveImg = override?.og_image || image || DEFAULT_IMAGE;
  const effectiveKeywords = override?.keywords || keywords || DEFAULT_KEYWORDS;

  const fullTitle = _buildFullTitle(effectiveTitle);
  const desc = effectiveDesc;
  // Normalize path: strip trailing slash (except root) to keep canonical
  // identical to the URL listed in sitemap.xml. Google would otherwise treat
  // `/services` and `/services/` as two URLs sharing the same content.
  let normPath = path && path !== '/' ? path.replace(/\/+$/, '') : path;
  // `/waschboxen` and `/waschbox-finder` render the exact same component
  // (see App.js) — point the legacy URL's canonical at the primary one so
  // Google consolidates both as a single indexed page instead of flagging
  // duplicate content.
  if (normPath === '/waschboxen') normPath = '/waschbox-finder';
  const url = normPath ? `${BASE_URL}${normPath}` : BASE_URL;
  const img = effectiveImg;

  // BreadcrumbList aus { name, path }-Array
  const breadcrumbLd = breadcrumbs && breadcrumbs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((b, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": b.name,
      "item": b.path ? `${BASE_URL}${b.path}` : undefined,
    })),
  } : null;

  // Cleanup: entfernt die statischen index.html-Defaults sobald React seine
  // eigene (per-Route) Version des Tags gerendert hat. Sonst sieht Google
  // zwei "<title>" oder "<meta name=description>"-Elemente (bzw. zwei
  // rel=canonical) und ignoriert bei mehreren Canonicals unvorhersehbar
  // eines davon. WICHTIG: React 19 rendert <title>/<meta>/<link> aus
  // react-helmet-async nativ ins <head> OHNE das früher übliche
  // `data-rh`-Attribut zu setzen — die alte Erkennung darüber fand daher nie
  // ein Match. Die index.html-Defaults sind stattdessen explizit mit
  // `data-default-seo="1"` markiert; die entfernen wir hier einfach direkt.
  useEffect(() => {
    document.head.querySelectorAll('[data-default-seo="1"]').forEach((e) => {
      try { e.remove(); } catch { /* noop */ }
    });
  });

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={effectiveKeywords} />
      <meta name="author" content={SITE_NAME} />
      <meta name="robots" content={noIndex ? 'noindex,nofollow' : 'index,follow'} />
      <link rel="canonical" href={url} />

      {/* Geo / Local SEO */}
      <meta name="geo.region" content="DE-SL" />
      <meta name="geo.placename" content="Eppelborn" />
      <meta name="geo.position" content="49.4027;6.9647" />
      <meta name="ICBM" content="49.4027, 6.9647" />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="de_DE" />

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {/* Optional Page-Specific Structured Data (z. B. FAQ, LocalBusiness-Liste) */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
      {breadcrumbLd && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbLd)}
        </script>
      )}
    </Helmet>
  );
};

// JSON-LD Structured Data for the business (placed once in App)
// Die 11 primär bedienten saarländischen Städte — wichtig für Google-Ranking
// bei „Aufbereiter <Stadt>" und „Aufbereiter in meiner Nähe".
const SERVICE_CITIES = [
  { name: 'Eppelborn',    lat: 49.4027, lon: 6.9647 },
  { name: 'Saarbrücken',  lat: 49.2354, lon: 6.9969 },
  { name: 'Neunkirchen',  lat: 49.3474, lon: 7.1794 },
  { name: 'Saarlouis',    lat: 49.3134, lon: 6.7519 },
  { name: 'Homburg',      lat: 49.3253, lon: 7.3369 },
  { name: 'Merzig',       lat: 49.4437, lon: 6.6389 },
  { name: 'St. Wendel',   lat: 49.4664, lon: 7.1687 },
  { name: 'Völklingen',   lat: 49.2513, lon: 6.8583 },
  { name: 'Sulzbach',     lat: 49.3011, lon: 7.0538 },
  { name: 'Dillingen',    lat: 49.3547, lon: 6.7286 },
  { name: 'Lebach',       lat: 49.4103, lon: 6.9089 },
];

// Convert "08:00 - 18:00" | "Geschlossen" → {opens, closes} | null
const _parseHours = (str) => {
  if (!str || typeof str !== 'string') return null;
  const s = str.trim().toLowerCase();
  if (!s || s === 'geschlossen' || s === 'closed') return null;
  const m = s.match(/(\d{1,2}[:.]?\d{0,2})\s*[-–bis]+\s*(\d{1,2}[:.]?\d{0,2})/);
  if (!m) return null;
  const norm = (t) => t.replace('.', ':').padStart(5, '0').slice(0, 5).replace(/^(\d):/, '0$1:');
  return { opens: norm(m[1]), closes: norm(m[2]) };
};

const _DAY_MAP = {
  monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
  thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday',
};

// Baue openingHoursSpecification aus SiteSettings — gruppiert gleiche Zeiten.
const _buildOpeningHoursSpec = (openingHours) => {
  if (!openingHours) return [];
  const buckets = new Map();
  Object.entries(openingHours).forEach(([day, str]) => {
    const parsed = _parseHours(str);
    if (!parsed) return;
    const key = `${parsed.opens}-${parsed.closes}`;
    if (!buckets.has(key)) buckets.set(key, { ...parsed, days: [] });
    buckets.get(key).days.push(_DAY_MAP[day] || day);
  });
  return Array.from(buckets.values()).map(b => ({
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: b.days,
    opens: b.opens,
    closes: b.closes,
  }));
};

export const buildOpeningHoursSpec = _buildOpeningHoursSpec;

export const BusinessJsonLd = () => {
  const site = useSiteSettings();
  const biz = site?.getBusinessInfo?.() || {};
  const hours = site?.getOpeningHours?.() || {};
  const social = site?.getSocial?.() || {};

  // Fallback zu Konstanten falls SiteSettings noch nicht geladen ist
  const companyName = biz.company_name || 'CHROBE One World';
  const email       = biz.email || 'info@chrobe.de';
  const phone       = biz.phone || '06881/5959 023';
  const telE164     = `+49-${(phone || '').replace(/[^0-9]/g, '').replace(/^0/, '')}`;
  const street      = biz.street || 'Am Hirtenhaus 17';
  const city        = biz.city || 'Eppelborn';
  const zip         = biz.zip   || '66571';

  const openingSpec = _buildOpeningHoursSpec(hours);
  const sameAs = [
    'https://www.google.com/maps/search/?api=1&query=CHROBE+One+World+Eppelborn',
    ...(social?.instagram ? [social.instagram.startsWith('http') ? social.instagram : `https://instagram.com/${social.instagram.replace(/^@/, '')}`] : []),
  ];
  const ratingValue = Number(social?.google_rating_value);
  const reviewCount = parseInt(social?.google_review_count, 10);
  const hasRating = ratingValue > 0 && reviewCount > 0;

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": ["AutomotiveBusiness", "LocalBusiness", "Organization"],
        "@id": `${BASE_URL}/#organization`,
        "name": companyName,
        // Nur echte Namensvarianten. Orts-Keywords gehören nicht in den Firmennamen,
        // und "Aufbereiter Eppelborn" ist der Partnerbetrieb, nicht CHROBE.
        "alternateName": ["CHROBE", "One World"],
        "description": DEFAULT_DESC,
        "slogan": "Eine Welt. Ein Ansprechpartner. Alles aus einer Hand.",
        "foundingDate": "2024",
        ...(biz.vat_id ? { "vatID": biz.vat_id } : {}),
        ...(biz.tax_number ? { "taxID": biz.tax_number } : {}),
        // BMW Group Partnerbetrieb (kein Vertragshändler/-werkstatt) — Partnernummer b2io60418946
        "affiliation": {
          "@type": "Organization",
          "name": "BMW Group",
          "identifier": {
            "@type": "PropertyValue",
            "propertyID": "BMW Group Partnernummer",
            "value": "b2io60418946"
          }
        },
        "knowsAbout": [
          "Fahrzeugaufbereitung", "Detailing", "Keramikversiegelung", "Lackpolitur",
          "Lackkorrektur", "Innenraumreinigung", "Lederpflege", "Fahrzeugpflege für Flotten"
        ],
        "url": BASE_URL,
        "logo": DEFAULT_IMAGE,
        "image": DEFAULT_IMAGE,
        "telephone": telE164,
        "email": email,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": street,
          "addressLocality": city,
          "addressRegion": "Saarland",
          "postalCode": zip,
          "addressCountry": "DE"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 49.4027,
          "longitude": 6.9647
        },
        "hasMap": `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${companyName} ${street} ${zip} ${city}`)}`,
        "openingHoursSpecification": openingSpec.length > 0 ? openingSpec : [
          { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday"], "opens": "08:00", "closes": "18:00" },
          { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Friday","Saturday"], "opens": "08:00", "closes": "20:00" }
        ],
        "priceRange": "€€",
        "currenciesAccepted": "EUR",
        "paymentAccepted": "Cash, Credit Card, EC Card, Bank Transfer",
        // Explizite Städte-Auflistung — starkes Ranking-Signal für „Aufbereiter <Stadt>"
        "areaServed": SERVICE_CITIES.map((c) => ({
          "@type": "City",
          "name": c.name,
          "containedInPlace": { "@type": "State", "name": "Saarland" },
          "geo": { "@type": "GeoCoordinates", "latitude": c.lat, "longitude": c.lon }
        })),
        "serviceArea": {
          "@type": "GeoCircle",
          "geoMidpoint": { "@type": "GeoCoordinates", "latitude": 49.4027, "longitude": 6.9647 },
          "geoRadius": "50000",
          "description": "50 km rund um Eppelborn — komplettes Saarland inkl. angrenzendem Rheinland-Pfalz"
        },
        "sameAs": sameAs,
        ...(hasRating ? {
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": ratingValue,
            "reviewCount": reviewCount,
            "bestRating": 5,
            "worstRating": 1
          }
        } : {}),
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": `${companyName} — Services & Produkte`,
          "itemListElement": [
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Fahrzeugaufbereitung Saarland", "description": "Komplette Auto-Aufbereitung — Außen und Innen, markenoffen für alle Fahrzeuge" }},
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Keramikversiegelung", "description": "Langzeitschutz für den Lack mit Keramikbeschichtung — bis zu 5 Jahre" }},
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Lackpolitur & Lackkorrektur", "description": "1-Stufen und 3-Stufen Politur für perfekten Glanz" }},
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Detailing / Full Detail Paket", "description": "Komplette Innen- und Außenaufbereitung auf Profi-Niveau" }},
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Innenraumreinigung & Lederpflege", "description": "Tiefenreinigung, Fleckenentfernung und Ledermanufaktur-Pflege" }},
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Pflege-Abo Auto", "description": "Monatliches Pflege-Abonnement mit bevorzugter Terminvergabe" }},
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Mobile Fahrzeugpflege", "description": "Aufbereiter kommt zu dir nach Hause oder ins Büro — im ganzen Saarland" }},
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Flottenmanagement", "description": "Aufbereitung für Geschäftskunden, Fuhrparks und Autohäuser" }},
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "MyWorld Garage-Cockpit", "description": "Digitale Garage mit KI-Lackschaden-Scanner, Pflege-Historie und Care-Card" }}
          ]
        }
      })}</script>
    </Helmet>
  );
};

export default SEO;
